'use client';

import { useCallback, useEffect, useState } from 'react';
import type { SignalCategoryType } from '@terra/shared-schemas-orchestrator';
import {
  areSignalCategoriesInScopeDirty,
  resolveStoredSignalCategoriesInScope,
} from '../../settings/testing-scope/signal-category-scope';
import {
  pickInternalNetworkScopeLists,
  shouldAutoSaveInternalNetworkScopeLists,
  type InternalNetworkScopeListSection,
  type InternalNetworkScopeLists,
  type InternalNetworkScopePatch,
  type InternalNetworkScopeStored,
} from './internal-scope.model';

const AUTO_SAVE_DELAY_MS = 600;

export type UseInternalNetworkScopeDraftParams = {
  stored: InternalNetworkScopeStored;
  isLocked: boolean;
  isSaving: boolean;
  onAutoSave: (patch: InternalNetworkScopePatch) => Promise<void>;
};

export type InternalNetworkScopeDraft = {
  lists: InternalNetworkScopeLists;
  onListChange: (params: {
    section: InternalNetworkScopeListSection;
    values: string[];
  }) => void;
  signalCategoriesInScope: SignalCategoryType[];
  onSignalCategoriesChange: (categories: SignalCategoryType[]) => void;
};

const resolveSignalCategoriesDraft = (stored: SignalCategoryType[]) =>
  resolveStoredSignalCategoriesInScope({
    stored,
    targetType: 'INTERNAL_NETWORK',
  });

export const useInternalNetworkScopeDraft = ({
  stored,
  isLocked,
  isSaving,
  onAutoSave,
}: UseInternalNetworkScopeDraftParams): InternalNetworkScopeDraft => {
  const [storedSnapshot, setStoredSnapshot] = useState(stored);
  const [lists, setLists] = useState<InternalNetworkScopeLists>(() =>
    pickInternalNetworkScopeLists(stored),
  );
  const [signalCategoriesInScope, setSignalCategoriesInScope] = useState<
    SignalCategoryType[]
  >(() => resolveSignalCategoriesDraft(stored.signalCategoriesInScope));

  if (storedSnapshot !== stored) {
    setStoredSnapshot(stored);
    setLists(pickInternalNetworkScopeLists(stored));
    setSignalCategoriesInScope(
      resolveSignalCategoriesDraft(stored.signalCategoriesInScope),
    );
  }

  const shouldSaveLists = shouldAutoSaveInternalNetworkScopeLists({
    draft: lists,
    stored,
    isLocked,
    isSaving,
  });
  const shouldSaveSignalCategories =
    !isLocked &&
    !isSaving &&
    areSignalCategoriesInScopeDirty({
      draft: signalCategoriesInScope,
      stored: stored.signalCategoriesInScope,
      targetType: 'INTERNAL_NETWORK',
    });

  useEffect(() => {
    if (!shouldSaveLists) {
      return;
    }

    const timerId = window.setTimeout(() => {
      void onAutoSave(lists);
    }, AUTO_SAVE_DELAY_MS);

    return () => window.clearTimeout(timerId);
  }, [lists, onAutoSave, shouldSaveLists]);

  useEffect(() => {
    if (!shouldSaveSignalCategories) {
      return;
    }

    const timerId = window.setTimeout(() => {
      void onAutoSave({ signalCategoriesInScope });
    }, AUTO_SAVE_DELAY_MS);

    return () => window.clearTimeout(timerId);
  }, [onAutoSave, shouldSaveSignalCategories, signalCategoriesInScope]);

  const onListChange = useCallback(
    ({
      section,
      values,
    }: {
      section: InternalNetworkScopeListSection;
      values: string[];
    }) => {
      setLists((current) => ({ ...current, [section]: values }));
    },
    [],
  );

  return {
    lists,
    onListChange,
    signalCategoriesInScope,
    onSignalCategoriesChange: setSignalCategoriesInScope,
  };
};
